/**
 * AdminDemoRequests — inbound /demo form leads, filterable by status.
 *
 * Layout: page header + status segmented control + table-ish list.
 * Each row: name + center, email, range/country, received_at, status pill,
 * actions to move New → Contacted → Closed.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import AdminSidebar from "../../components/AdminSidebar";
import { Mail, Calendar, MapPin, Inbox, Check } from "lucide-react";

const STATUS_META = {
  new: { label: "New", bg: "#E6EFE8", fg: "#1F3A2A" },
  contacted: { label: "Contacted", bg: "#FFF4D6", fg: "#B45309" },
  closed: { label: "Closed", bg: "#DFF5E1", fg: "#1F8A3F" },
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminDemoRequests() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchRows = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status_filter: filter };
      const r = await api.get("/admin/demo-requests", { params });
      setRows(r.data || []);
    } catch (_e) {
      setRows([]);
    }
  }, [filter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  async function setStatus(id, status) {
    try {
      await api.patch(`/admin/demo-requests/${id}`, { status });
      toast.success("Status updated.");
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A quiet hiccup.");
    }
  }

  return (
    <div className="min-h-screen flex bg-[#F7F8FC]" data-testid="admin-demo-requests-page">
      <AdminSidebar />
      <main className="flex-1 min-w-0 px-6 sm:px-10 py-10">
        <header className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <span className="eyebrow">Inbox</span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight leading-tight text-[#1A1B2E]">
              Demo requests
            </h1>
            <p className="mt-2 text-sm text-[#6B7280] max-w-xl">
              Inbound /demo form leads. Reply within one business day.
            </p>
          </div>
          <div
            data-testid="demo-requests-filter"
            className="inline-flex items-center bg-white border hairline rounded-full p-1"
          >
            {[
              { k: "all", label: "All" },
              { k: "new", label: "New" },
              { k: "contacted", label: "Contacted" },
              { k: "closed", label: "Closed" },
            ].map((f) => (
              <button
                key={f.k}
                type="button"
                onClick={() => setFilter(f.k)}
                data-testid={`demo-requests-filter-${f.k}`}
                className={
                  "px-4 py-1.5 rounded-full text-sm font-medium transition-colors " +
                  (filter === f.k
                    ? "bg-[#1F3A2A] text-white"
                    : "text-[#6B7280] hover:text-[#1A1B2E]")
                }
              >
                {f.label}
              </button>
            ))}
          </div>
        </header>

        <section className="mt-8">
          {rows === null ? (
            <div className="text-sm text-[#9CA3AF]">Loading…</div>
          ) : rows.length === 0 ? (
            <div
              data-testid="demo-requests-empty"
              className="rounded-3xl bg-white border hairline p-12 text-center"
            >
              <Inbox
                size={28}
                strokeWidth={1.3}
                className="mx-auto text-[#9CA3AF]"
              />
              <p className="mt-5 text-[#6B7280] italic">
                No demo requests yet. They'll appear here as the form is used.
              </p>
            </div>
          ) : (
            <ul
              data-testid="demo-requests-list"
              className="rounded-3xl bg-white border hairline overflow-hidden divide-y divide-[#EEF0F4]"
            >
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.new;
                return (
                  <li
                    key={r.id}
                    data-testid={`demo-request-row-${r.id}`}
                    className="px-5 py-4 sm:px-7 sm:py-5"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-3 flex-wrap">
                          <div className="font-semibold text-[#1A1B2E] text-base">
                            {r.name}
                          </div>
                          <div className="text-sm text-[#1F3A2A]">{r.center}</div>
                          <span
                            data-testid={`demo-request-status-${r.id}`}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold"
                            style={{ background: meta.bg, color: meta.fg }}
                          >
                            {meta.label}
                          </span>
                          {r.email_dispatched && (
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#1F8A3F]">
                              <Check size={12} strokeWidth={2} />
                              Sales notified
                            </span>
                          )}
                        </div>
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs text-[#6B7280]">
                          <span className="inline-flex items-center gap-1.5">
                            <Mail size={12} strokeWidth={1.6} />
                            <a
                              href={`mailto:${r.email}`}
                              className="hover:text-[#1F3A2A]"
                            >
                              {r.email}
                            </a>
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin size={12} strokeWidth={1.6} />
                            {r.country} · {r.range}
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar size={12} strokeWidth={1.6} />
                            {formatDate(r.received_at)}
                          </span>
                        </div>
                        {r.notes && (
                          <p className="mt-3 text-sm text-[#1A1B2E] italic border-l-2 border-[#E6EFE8] pl-3 max-w-2xl">
                            {r.notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {r.status !== "contacted" && (
                          <button
                            type="button"
                            data-testid={`demo-request-mark-contacted-${r.id}`}
                            onClick={() => setStatus(r.id, "contacted")}
                            className="text-xs px-3 py-1.5 rounded-full bg-[#FFF4D6] text-[#B45309] hover:bg-[#FFE4D6] font-medium"
                          >
                            Mark contacted
                          </button>
                        )}
                        {r.status !== "closed" && (
                          <button
                            type="button"
                            data-testid={`demo-request-mark-closed-${r.id}`}
                            onClick={() => setStatus(r.id, "closed")}
                            className="text-xs px-3 py-1.5 rounded-full bg-[#DFF5E1] text-[#1F8A3F] hover:bg-[#C5EDD0] font-medium"
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
